import React from 'react';
import { TrendingUp, AlertCircle, Check } from 'lucide-react';

interface ConfidenceBadgeProps {
  confidence: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

/**
 * ConfidenceBadge - Visual indicator for extraction confidence scores
 *
 * Color coding aligned with adaptive learning:
 * - Green (85-92%): High confidence, well-learned pattern
 * - Yellow (70-84%): Medium confidence, learning in progress
 * - Orange (60-69%): Low-medium, needs more feedback
 * - Red (<60%): Low confidence, please review
 */
const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  confidence,
  size = 'md',
  showIcon = true,
  className = ''
}) => {
  // Normalize confidence to 0-100 range
  const normalizedConfidence = confidence > 1 ? confidence : confidence * 100;
  const confidencePercent = Math.round(normalizedConfidence);

  // Determine confidence level and styling
  const getConfidenceLevel = () => {
    if (confidencePercent >= 85) {
      return {
        level: 'high',
        label: 'High Confidence',
        bgColor: 'bg-green-500/20',
        borderColor: 'border-green-500/40',
        textColor: 'text-green-300',
        icon: <Check className="w-3 h-3" />
      };
    } else if (confidencePercent >= 70) {
      return {
        level: 'medium',
        label: 'Learning',
        bgColor: 'bg-yellow-500/20',
        borderColor: 'border-yellow-500/40',
        textColor: 'text-yellow-300',
        icon: <TrendingUp className="w-3 h-3" />
      };
    } else if (confidencePercent >= 60) {
      return {
        level: 'low-medium',
        label: 'Needs Feedback',
        bgColor: 'bg-orange-500/20',
        borderColor: 'border-orange-500/40',
        textColor: 'text-orange-300',
        icon: <TrendingUp className="w-3 h-3" />
      };
    } else {
      return {
        level: 'low',
        label: 'Please Review',
        bgColor: 'bg-red-500/20',
        borderColor: 'border-red-500/40',
        textColor: 'text-red-300',
        icon: <AlertCircle className="w-3 h-3" />
      };
    }
  };

  const confidenceInfo = getConfidenceLevel();

  // Size variants
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs gap-1',
    md: 'px-2 py-1 text-sm gap-1.5',
    lg: 'px-3 py-1.5 text-base gap-2'
  };

  return (
    <div
      className={`
        inline-flex items-center rounded-full border
        ${confidenceInfo.bgColor}
        ${confidenceInfo.borderColor}
        ${confidenceInfo.textColor}
        ${sizeClasses[size]}
        ${className}
      `}
      title={`${confidenceInfo.label}: ${confidencePercent}% confidence`}
    >
      {showIcon && confidenceInfo.icon}
      <span className="font-semibold">{confidencePercent}%</span>
    </div>
  );
};

export default ConfidenceBadge;
